import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} PreSellPropertyRefs
 * @property {HTMLInputElement} input - The hidden `Will Ship` line item property input.
 * @property {HTMLScriptElement} [dates] - JSON map of variant id to formatted ship date.
 */

/**
 * Keeps the hidden `Will Ship` line item property in step with the selected variant.
 *
 * A variant change only morphs the variant picker and the add-to-cart button, so the
 * rest of the product form — this input included — is never re-rendered. The ship date
 * for every pre-sell variant is therefore embedded up front and looked up by id here.
 *
 * The input is disabled when the selected variant has no pre-sell date, which keeps it
 * out of the submitted form data rather than sending an empty property.
 *
 * @extends Component<PreSellPropertyRefs>
 */
class PreSellPropertyComponent extends Component {
  requiredRefs = ['input'];

  #abortController = new AbortController();

  /** @type {Record<string, string> | undefined} */
  #dates;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * The ship dates for this product's pre-sell variants, keyed by variant id.
   * @returns {Record<string, string>}
   */
  get #shipDates() {
    if (this.#dates) return this.#dates;

    try {
      this.#dates = JSON.parse(this.refs.dates?.textContent || '{}');
    } catch {
      this.#dates = {};
    }

    return this.#dates ?? {};
  }

  /**
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    // A combined listing that swaps in a different product re-renders the whole of
    // `main` from the server, so this component arrives with the new product's dates
    // already embedded and there is nothing to reconcile here.
    if (event.detail.data.newProduct) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variantId = event.detail.resource?.id;

    this.#setShipDate(variantId == null ? undefined : this.#shipDates[String(variantId)]);
  };

  /**
   * @param {string} [shipDate] - The ship date, or undefined when the variant is not pre-sell.
   */
  #setShipDate(shipDate) {
    const { input } = this.refs;

    input.value = shipDate ?? '';
    input.disabled = !shipDate;
  }
}

if (!customElements.get('pre-sell-property-component')) {
  customElements.define('pre-sell-property-component', PreSellPropertyComponent);
}
